import { IconButton, Avatar, ImageListItem, List, Select, Dialog, DialogActions, DialogContent, Card, Button, Typography, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import Box from 'src/components/MuiBox';
import Grid from 'src/components/MuiGrid';
import { phoneNumberFieldStyles, clearButtonStyles, fieldImageItemStyles, fieldImageLineWrapperStyles, itemDeleteButtonStyles, languageSwitcherWrapperStyles, loadingWrapperStyles, fieldListStyles, disabledErrorStyles, iconContentStyles, imageInputStyles, authFormWrapperStyles, authFormWrapperWithMainContentStyles, nestedRowAddButtonStyles, advancedToggleTextStyles } from 'src/styles/form';
import { dialogCloseButtonStyles, gridDialogShellStyles, formDialogShellStyles, compactDialogShellStyles, compactInfoCardDialogShellStyles } from 'src/styles/dialog';
import { datePickerBoxStyles, datepickerInputStyles } from 'src/styles/datepicker';
import { errorBlinkKeyframes, imageAvatarStyles } from 'src/styles/animations';

/**
 * AG Grid 목록 + 고정 하단 액션바 다이얼로그 셸 정본 — EventSlack/SlackConfigCopyModal
 * (SlackConfigCopyDialog/Content/Actions) ↔ DeviceProfile/DeviceAssignModal
 * (DeviceAssignDialog/Content/Actions) 2곳 파일 전체 바이트 동일 → 이 정본을 그대로 alias.
 */
export const GridDialogShellBase = styled(Dialog)(
  ({ theme }: any) => gridDialogShellStyles(theme),
);

/** 그리드 영역만 남은 높이를 쓰고, 하단 DialogActions는 항상 노출 */
export const GridDialogContentBase = styled(DialogContent)(
  () => `
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-top: 12px;
`,
);

export const GridDialogActionsBase = styled(DialogActions)(
  ({ theme }: any) => `
  flex-shrink: 0;
  border-top: 1px solid ${theme.palette.divider};
`,
);

/**
 * TextField shrink 라벨이 잘리지 않도록 overflow:visible을 유지하는 폼 다이얼로그 셸 정본 —
 * DeviceProfile::ProfileCopyDialog / EventSlack/SlackConfigFormModal::SlackConfigDialog /
 * EventSetting/MgmtStateEventFormModal::MgmtStateEventDialog / EventSetting/
 * NoiseFilterFormModal::NoiseFilterDialog / DeviceTypeList::DeviceTypeDialog 5곳
 * 바이트 동일(export 이름만 다름) → 이 정본을 그대로 alias.
 */
export const FormDialogShellBase = styled(Dialog)(
  ({ theme }: any) => formDialogShellStyles(theme),
);

/**
 * 다이얼로그 타이틀 여백만 제거하는 컴팩트 셸 정본 — ConfirmModal::ConfirmModalDialog ↔
 * AgGrid::EditModalDialog 2곳 바이트 동일(export 이름만 다름) → 이 정본을 그대로 alias.
 */
export const CompactDialogShellBase = styled(Dialog)(
  ({ theme }: any) => compactDialogShellStyles(theme),
);

/**
 * 컴팩트 셸 + info_card + lot_button 조합 정본 — mes/Defect::DefectInputModalDialog ↔
 * mes/Stock::StockAdjustmentModalDialog 2곳 바이트 동일(export 이름만 다름) →
 * 이 정본을 그대로 alias.
 */
export const CompactInfoCardDialogShellBase = styled(Dialog)(
  ({ theme }: any) => compactInfoCardDialogShellStyles(theme),
);

/**
 * data_category/sub_category 라벨 체계를 쓰는 폼 행 정본 — setting/Admin::SettingAdminFormLineWrapper
 * ↔ setting/Preference::SettingPreferenceFormLineWrapper 2곳 바이트 동일(export 이름만 다름) →
 * 이 정본을 그대로 alias.
 */
export const SettingFormLineWrapperBase = styled(Grid)(
  ({ theme }: any) => `
  height: 66px;
  align-content: center;

  .data_category {
    font-weight : bold;
    font-size: 1.1rem;
  }
  .data_category::before {
    content: "● ";
    color: ${theme.colors.primary.main};
  }

  .sub_category {
    // margin-top: -8px;
    font-weight: bold;
    font-size: 0.9rem;
    margin-left: 0.7rem;
  }
  .sub_category::before {
    content: "○ ";
    color: ${theme.colors.primary.main};
  }

  .setting_data {
    margin-top: -16px;
    margin-left: 1.2rem;
  }

  .label {
    font-weight: bold;
  }

  .MuiInputBase-root{
    height: 40px;
  }
`,
);

/**
 * 가운데 정렬 flex 박스 정본 — DataDisplayCard::LoadingSpinnerWrap ↔
 * monitoring/DeviceDetail/AlarmSettingTable::CenterFlexBox 2곳 바이트 동일(베이스만
 * styled('div') vs styled(Box)로 달랐으나 렌더 결과·CSS 출력 동일) → 이 정본을 그대로 alias.
 */
export const CenterFlexBoxBase = styled(Box)(
  () => `
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);

/**
 * 국가코드+전화번호 입력 레이아웃 골격 정본 — 기본 파라미터(전 옵션 미지정) 버전.
 * ManufacturerList/ProductList/InspectionDetail/MaintenanceDetail/setting/Account
 * 5곳 바이트 동일 → 이 정본을 그대로 alias.
 * 다른 파라미터 조합이 필요한 페이지는 `styled(PhoneNumberFieldBase)`가 아니라
 * `phoneNumberFieldStyles({...})`를 직접 호출하는 새 변형 상수를 이 파일에 추가해 확장한다
 * (파라미터 자체가 CSS 분기를 좌우하므로 emotion 확장이 아닌 별도 styled 선언으로 처리).
 *
 * 과거 `PhoneNumberFieldButtonMarginTop5px`라는 별도 export가 있었으나, 국기 아이콘 버튼
 * margin-top:5px 오버라이드가 회귀 수정으로 제거된 뒤 이 정본과 완전히 동일한 바디만 남아
 * 있었음 → 중복 export를 제거하고 이 정본으로 병합.
 */
export const PhoneNumberFieldBase = styled(Box)(
  () => phoneNumberFieldStyles(),
);

/**
 * 필드 지우기(X) 아이콘 버튼 정본 — marginLeft:'2.05rem' + marginTop:'-0.25rem' 조합.
 * location(ClearButtonInModal)/InspectionList/MaintenanceList/MaintenanceDetail 4곳 바이트 동일 → 이 정본을 그대로 alias.
 */
export const ClearButtonMarginLeft205rem = styled(IconButton)(
  ({ theme, errorState }: any) => `
  ${clearButtonStyles({ theme, errorState, marginLeft: '2.05rem', marginTop: '-0.25rem', hoverBackground: alpha(theme.palette.primary.main, 0.45) })}
  ${errorState && errorBlinkKeyframes}
`,
);

/**
 * 이미지 업로드 아바타 프리뷰 정본 — imageAvatarStyles(errorState, theme.colors.alpha.black[50]).
 * InspectionDetail/InspectionList/MaintenanceList/MaintenanceDetail 4곳 바이트 동일 → 이 정본을 그대로 alias.
 * location은 색상 인자가 다른 고정값('#8F96A4')이라 별도 정의 유지(T3-like 단일 파일 변형).
 */
export const ImageAvatarBlack50 = styled(Avatar)(
  ({ errorState, theme }: any) => imageAvatarStyles(errorState, theme.colors.alpha.black[50]),
);

/**
 * 필드 이미지 썸네일(ImageListItem) 정본 — fieldImageItemStyles(theme).
 * location/InspectionDetail/MaintenanceDetail 3곳 바이트 동일 → 이 정본을 그대로 alias.
 */
export const FieldImageItemBase = styled(ImageListItem)(
  ({ theme }: any) => fieldImageItemStyles(theme),
);

/**
 * 필드 이미지 줄 래퍼(Grid) 정본 — fieldImageLineWrapperStyles() 인자 없음(기본 breakpoint).
 * InspectionDetail/MaintenanceDetail 2곳 바이트 동일 → 이 정본을 그대로 alias.
 * location은 '599px' 인자를 전달하는 별도 변형이라 자체 정의 유지.
 */
export const FieldImageLineWrapperBase = styled(Grid)(
  () => fieldImageLineWrapperStyles(),
);

/**
 * 항목 삭제(X) 아이콘 버튼 정본 — itemDeleteButtonStyles(theme).
 * location/InspectionDetail/InspectionList 3곳 바이트 동일 → 이 정본을 그대로 alias.
 * mes/Item·mes/Manufacturing·DeviceDetail·ElectricVehicleCharger는 완전히 다른 커스텀 CSS라 T3(제외) — 손대지 않음.
 */
export const ItemDeleteButtonBase = styled(IconButton)(
  ({ theme }: any) => itemDeleteButtonStyles(theme),
);

/**
 * 언어 선택 위젯 래퍼 정본 — support/account/setting-Admin/setting-components/exception 5곳 바이트 동일.
 * 이 정본을 그대로 alias.
 */
export const LanguageSwitcherWrapperBase = styled(Box)(() => languageSwitcherWrapperStyles);

/**
 * 로딩 스피너 래퍼 정본 — support/account/setting-Admin/setting-components 4곳 바이트 동일.
 * AlarmSettingTable은 완전히 다른 커스텀 CSS라 T3(제외) — 손대지 않음.
 */
export const LoadingWrapperBase = styled(Box)(() => loadingWrapperStyles);

/**
 * 알람 단위 Select 정본 — setting/Admin↔setting/Preference 2곳 바이트 동일.
 */
export const AlarmUnitListBase = styled(Select)(
  () => `
  margin-top: 2px;
  margin-right: -5px;
  `,
);

/**
 * 날짜 선택 입력 박스 정본(datePickerBoxStyles(theme)) — setting/Admin↔setting/Preference 2곳 바이트 동일.
 * PageInput의 DatePickerBox는 shouldForwardProp·제네릭 props를 갖는 완전히 다른 컴포넌트라 T3(제외).
 */
export const DatePickerBoxBase = styled(Box)(
  ({ theme }: any) => `
  ${datePickerBoxStyles(theme)}
  `,
);

/**
 * 필드 목록(List) 골격 정본 — fieldListStyles(theme) 옵션 없음.
 * Admin/DeviceParameters-ParameterInfo↔Admin/DeviceProfile-ProfileInfo 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const FieldListBase = styled(List)(
  ({ theme }: any) => fieldListStyles(theme),
);

/**
 * withActionTakenLabel:true 변형 — setting/Account↔monitoring/DeviceDetail 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const FieldListWithActionTakenLabel = styled(List)(
  ({ theme }: any) => fieldListStyles(theme, { withActionTakenLabel: true }),
);

/**
 * 정비 이력 등록 다이얼로그 골격 정본 — MaintenanceList 전체 CSS와 바이트 동일.
 * InspectionList는 이 골격 + 추가 3블록(.image_upload_button/.item_add_button/.image_div)만 확장한다.
 * InspectionDetail/location은 datepickerInputStyles 인자·iconContentStyles breakpoint·에러 블록 구조가
 * 근본적으로 달라 T3(제외) — 손대지 않음.
 */
export const CreateMaintenanceLogDialogBase = styled(Dialog)(
  ({ theme }: any) => `
  .MuiDialogTitle-root {
    padding: 27px;
  }
  .manufacturer_new_info_card {
    padding: 27px !important;
  }
  ${dialogCloseButtonStyles(theme, { right: '27px', top: '27px', withPadding: true })}
  .tag_chip_box {
    display: flex;
    flex-wrap: wrap;
    gap: 4.5px !important;
  }
  .address_grid {
    padding: 2px 27px 27px 27px;
  }
  ${datepickerInputStyles(theme, { borderRadius: '7px', fontFamilyFirst: true })}
  .datepicker_error {
    width: 100%;
    height: 53.13px;
    border: 1px solid red;
    border-radius: 7px;
    outline: none;
    &:hover {
      border: 1px solid ${theme.colors.alpha.black[50]};
    }
  }
  .datepicker_error::placeholder {
    color: red;
  }
  .react-datepicker-wrapper {
    width: 100%;
    .react-datepicker__close-icon {
      margin-right: 0.85rem;
    }
  }
  ${iconContentStyles({ withOpacity: true })}
  ${disabledErrorStyles}
  ${imageInputStyles}
  .MuiSelect-icon {
    margin-right: 0.47rem;
  }
  `,
);

/**
 * 인증 폼 카드 정본 — account::AccountAuthFormWrapper ↔ support::SupportAuthFormWrapper
 * 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const AuthFormWrapperBase = styled(Card)(
  () => authFormWrapperStyles(),
);

/**
 * 인증 폼 카드 변형(.form_content display:flex + .main_content) 정본 —
 * setting/Admin::SettingAdminAuthFormWrapper ↔ setting/components::AuthFormWrapper
 * 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const AuthFormWrapperWithMainContentBase = styled(Card)(
  () => authFormWrapperWithMainContentStyles(),
);

/**
 * 항목 추가 pill 버튼 정본 — setting/Admin::NestedRowAddButton ↔ mes/Item::ItemDetialAddButton
 * 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const NestedRowAddButtonBase = styled(Button)(
  ({ theme }: any) => nestedRowAddButtonStyles(theme),
);

/**
 * 고급 설정 토글 텍스트 정본 — InspectionDetail::InspectionDetailAdvancedToggleText ↔
 * MaintenanceDetail::MaintenanceDetailAdvancedToggleText 2곳 바이트 동일. 이 정본을 그대로 alias.
 */
export const AdvancedToggleTextBase = styled(Typography)(
  () => advancedToggleTextStyles,
);
